import { TimePicker } from "antd";
import dayjs from "dayjs";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import { TagsInput } from "react-tag-input-component";
import {
  useGetExercisesId,
  useUpdateExercises,
} from "../../api/useProducts/useExercises";
import {
  CustomButton,
  FileUploadModal,
  InputField,
  Sidebar,
} from "../../components";
import CustomBreadcrumb from "../../components/CustomBreadcrumb";
import FileUploadIcon from "../../components/FileUploadIcon";
import Loader from "../../components/Loader";
import { CategoryOption } from "../../config/Category";
import { useDebounce } from "../../hook/useDebounce";
import useMultipleFileUpload from "../../hook/useMultipleFileUpload";
import useSingleFileUpload from "../../hook/useSingleFileUpload";
import TagInput from "../../components/TagInput";

interface UploadedData {
  url?: string;
  file?: File | null;
}
const UpdateExercises = () => {
  const { id } = useParams();
  const { exerciseData, exerciseLoading, exerciseRefetch } = useGetExercisesId(
    Number(id)
  );
  const { mutate, isPending } = useUpdateExercises(Number(id));
  const { uploadImage, isUploading } = useSingleFileUpload();
  const { MultipleUploadImages, isMultipleUploading } = useMultipleFileUpload();
  const debouncedOnChange = useDebounce((value) => {
    setValue("tags", value); // Update the form state with the new tags
  }, 300);
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    control,
    watch,
    setError,
    clearErrors,
    setValue,
    resetField,
    formState: { errors },
  } = useForm();
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [uploadedData, setUploadedData] = useState<UploadedData>({});
  const [uploadImages, setUploadImages] = useState<File[]>([]);
  const handleFileUpload = (data: UploadedData) => {
    setUploadedData(data);
  };
  // const instructions = watch("instructions");
  const selectedCategory = watch("category");
  useEffect(() => {
    if (selectedCategory === "Cardio") {
      resetField("reps");
    } else {
      resetField("duration");
    }
  }, [selectedCategory, resetField]);
  const handleImageUpload = (e: any) => {
    if (e.target.files) {
      setUploadImages(Array.from(e.target.files)); // Store files as an array
    }
  };
  const onSubmit = async (data: any) => {
    let imageUrl;
    if (uploadedData.file) {
      imageUrl = await uploadImage(uploadedData.file);
    }
    let multipleImages;
    if (uploadImages.length > 0) {
      multipleImages = await MultipleUploadImages(uploadImages);
    }
    mutate(
      {
        ...(data?.title && { title: data.title }),
        ...(data?.tags && { tags: data.tags }),
        ...((uploadedData?.file || uploadedData?.url) && {
          video: uploadedData?.file ? imageUrl : uploadedData.url,
        }),
        ...(data?.instructions && { instructions: data.instructions }),
        ...(data?.category && { category: data.category }),
        ...(data?.reps && { reps: data.reps }),
        ...(data?.calories && { calories: data.calories }),
        ...(data?.sets && { sets: data.sets }),
        ...(data.duration && {
          // @ts-ignore
          duration: moment(data?.duration?.$d).format("HH:mm:ss"),
        }),
        ...(uploadImages.length > 0 && {
          images: multipleImages.map((value: string, index: number) => ({
            id: index,
            image: value,
          })),
        }),
      },
      {
        onSuccess: (data) => {
          toast.success("successfully created");
          navigate("/exercises");
        },
        onError: (error: any) => {
          toast.error(error.response.data.message);
        },
      }
    );
  };
  const onChangeHandler = useCallback(
    (value: string[]) => {
      debouncedOnChange(value);
    },
    [debouncedOnChange]
  );
  useEffect(() => {
    exerciseRefetch();
  }, [id]);
  useEffect(() => {
    if (exerciseData) {
      setValue("instructions", exerciseData?.instructions);
      setValue("tags", exerciseData?.tags);
      setValue("title", exerciseData?.title);
      setValue("category", exerciseData?.category);
      setValue("reps", exerciseData?.reps);
      setValue("sets", exerciseData?.sets);
      setValue("calories", exerciseData?.calories);

      // @ts-ignore
      // setValue("duration", exerciseData?.duration);

      // setUploadedData((prev)=> prev.url === exerciseData?.video);
    }
  }, [exerciseData, id]);
  const breadcrumbItems = [
    { title: "Exercises", link: "exercises" },
    { title: "Edit " },
  ];
  return (
    <Sidebar>
      <div className="mt-3">
        <CustomBreadcrumb items={breadcrumbItems} />
      </div>
      {exerciseLoading ? (
        <Loader />
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="mt-6">
          <div className="grid lg:grid-cols-2 grid-cols-1 gap-3 mt-4">
            <InputField
              label="Title"
              placeholder="Enter title"
              type="text"
              // name="title"
              register={register("title")}
              error={String(errors?.title?.message || "")}
            />
            <div>
              <p className="block text-gray-700 mb-2">Tags</p>
              <TagInput
                control={control}
                name="tags"
                addclass="mt-2 rounded-lg"
                defaultValue={exerciseData?.tags || []}
              />
              {/* <Controller
                name="tags"
                control={control}
                render={({ field }) => (
                  <TagsInput
                    value={field.value || []}
                    onChange={(value) => {
                      field.onChange(value);
                      onChangeHandler(value);
                    }}
                    placeHolder="type & then press Enter"
                    classNames={{
                      tag: "!bg-gray-200 !text-gray-500 !text-sm !ps-2",
                      input: "!w-full border !text-sm",
                    }}
                  />
                )}
              />
              {errors?.tags && (
                <p className="text-red-500 text-sm mt-1">
                  {String(errors?.tags?.message || "")}
                </p>
              )} */}
            </div>
            <div>
              <p className="block text-gray-700 mb-2">Category</p>
              <Controller
                name="category"
                control={control}
                // rules={{ required: "Category is required" }}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={CategoryOption}
                    value={CategoryOption.find(
                      (option) => option.value === field.value
                    )}
                    onChange={(selectedOption) =>
                      field.onChange(selectedOption?.value)
                    }
                    placeholder="Select category"
                    styles={{
                      control: (base) => ({
                        ...base,
                        height: "44px", // Background color for the select control
                        boxShadow: "none",
                        caretColor: "#24B1B3",
                        border: "1px solid #E5E7EB",
                        borderRadius: "8px",
                        fontSize: "14px",
                        "&:focus": {
                          outline: "none !important",
                          boxShadow: "none !important",
                          borderColor: "gray !important",
                        },
                        "&:hover": { borderColor: "none !important" },
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: "rgb(148 163 184)", // Set placeholder color here
                      }),
                    }}
                  />
                )}
              />
            </div>
            {selectedCategory === "Cardio" ? (
              <div>
                <p className="block text-gray-700 mb-2">Duration</p>
                {/* <Controller
                  name="duration"
                  control={control}
                  render={({ field }) => (
                    <TimePicker
                      {...field}
                      disableClock={true}
                      clearIcon={true}
                      format="HH:mm"
                      hourPlaceholder="HH"
                      minutePlaceholder="MM"
                      className="border bg-white h-11 px-2 !text-sm overflow-hidden border-gray-300 p-0 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500  placeholder-gray-400 custom-time-picker"
                    />
                  )}
                /> */}
                <Controller
                  name="duration"
                  control={control}
                  render={({ field }) => (
                    // @ts-ignore
                    <TimePicker
                      // @ts-ignore
                      defaultValue={dayjs(exerciseData?.duration, "HH:mm:ss")}
                      {...field}
                      className="!focus:shadow-none w-full h-11 !focus:border-gray-300 hover:shadow-none hover:border-gray-300 text-sm"
                    />
                  )}
                />
              </div>
            ) : (
              <>
                <InputField
                  disabled={!selectedCategory}
                  label="Reps"
                  placeholder="Enter reps"
                  type="number"
                  register={register(`reps`, {
                    validate: (value) => {
                      // @ts-ignore
                      if (value <= 0) {
                        return "Reps must be a positive number";
                      }
                      // @ts-ignore
                      if (!Number.isInteger(parseFloat(value))) {
                        return "Reps must be a whole number";
                      }
                      return true;
                    },
                  })}
                  error={String(errors.reps?.message || "")}
                />
              </>
            )}
            <InputField
              disabled={!selectedCategory}
              label="Sets"
              placeholder="Enter set"
              type="number"
              register={register(`sets`, {
                validate: (value) => {
                  // @ts-ignore
                  if (value <= 0) {
                    return "Sets must be a positive number";
                  }
                  // @ts-ignore
                  if (!Number.isInteger(parseFloat(value))) {
                    return "Sets must be a whole number";
                  }
                  return true;
                },
              })}
              error={String(errors.sets?.message || "")}
            />
            <InputField
              label="Calories"
              placeholder="Enter calories"
              type="text"
              register={register(`calories`, {
                validate: (value) => {
                  // @ts-ignore
                  if (value <= 0) {
                    return "calories must be a positive number";
                  }
                  // @ts-ignore
                  if (!Number.isInteger(parseFloat(value))) {
                    return "calories must be a whole number";
                  }
                  return true;
                },
              })}
              error={String(errors.calories?.message || "")}
            />
          </div>
          <div className="mt-2">
            <p className="block text-gray-700 mb-2">Description</p>
            <Controller
              name="instructions"
              control={control}
              // rules={{ required: "instructions is required" }}
              render={({ field }) => (
                <ReactQuill
                  {...field}
                  // onChange={(value) => {
                  //   field.onChange(value);
                  // }}
                  className="quill-editor"
                  modules={{
                    toolbar: [
                      ["bold", "italic", "underline", "strike"],
                      ["blockquote", "code-block"],
                      [{ header: 1 }, { header: 2 }],
                      [{ list: "ordered" }, { list: "bullet" }],

                      [{ size: ["small", false, "large", "huge"] }],
                      [{ header: [1, 2, 3, 4, 5, 6, false] }],
                      [{ color: [] }, { background: [] }],

                      [{ align: [] }],
                      ["clean"],
                    ],
                  }}
                />
              )}
            />
            {/* {errors.instructions && (
            <p className="text-red-500 text-sm mt-1">
              {errors.instructions.message}
            </p>
          )} */}
          </div>
          <div className="grid lg:grid-cols-2 grid-cols-1 gap-3 mt-4">
            <div>
              <p className="block text-gray-700 mb-2">Upload Images</p>

              <div className="mt-3">
                <div>
                  <label
                    htmlFor="images"
                    role="button"
                    className="flex flex-col p-3 justify-center items-center min-h-52 border-[1.5px] border-dashed border-gray-400 rounded-2xl"
                  >
                    {uploadImages?.length > 0 ? (
                      <div>
                        <p className="text-sm text-gray-600">
                          {uploadImages.map((file, index) => (
                            <p key={index}>• {file?.name}</p>
                          ))}
                        </p>
                      </div>
                    ) : (
                      <>
                        {exerciseData?.exerciseImages &&
                        exerciseData?.exerciseImages?.length > 0 ? (
                          <p className="text-sm text-gray-600 text-ellipsis overflow-hidden whitespace-nowrap max-w-[90%]">
                            {exerciseData?.exerciseImages
                              .map((file, index) => `• ${file.image}`)
                              .join(", ")}
                          </p>
                        ) : (
                          <div>
                            <FileUploadIcon />
                            <CustomButton
                              handleClick={() =>
                                document.getElementById("images")?.click()
                              }
                              btntext="Upload"
                              addclass="mt-3"
                            />
                          </div>
                        )}
                      </>
                    )}
                  </label>
                  <input
                    type="file"
                    id="images"
                    onChange={handleImageUpload}
                    multiple
                    accept={"image/jpeg, image/png, image/jpg"}
                    className="hidden"
                  />
                  {/* {errors.images && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.images.message}
                  </p>
                )} */}
                </div>
              </div>
            </div>
            <div>
              <p className="block text-gray-700 mb-2">Upload video</p>

              <div className="mt-3">
                <div onClick={() => setModalOpen(true)}>
                  <div
                    role="button"
                    className="flex flex-col justify-center items-center min-h-52 border-[1.5px] border-dashed border-gray-400 rounded-2xl"
                  >
                    {uploadedData?.url || uploadedData?.file ? (
                      <div>
                        {uploadedData.url ? (
                          // Display uploaded URL if present
                          <p className="text-center text-sm text-gray-600">
                            Uploaded URL: {uploadedData.url}
                          </p>
                        ) : uploadedData.file ? (
                          // Display uploaded file if present
                          <p className="text-center text-sm text-gray-600">
                            Uploaded File: {uploadedData.file.name}
                          </p>
                        ) : (
                          // Display upload prompt if nothing is uploaded
                          <>
                            <FileUploadIcon />
                            <CustomButton btntext="Upload" addclass="mt-3" />
                          </>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-600 text-ellipsis overflow-hidden whitespace-nowrap max-w-[90%]">
                        {exerciseData?.video ? (
                          exerciseData?.video
                        ) : (
                          <>
                            <FileUploadIcon />
                            <CustomButton btntext="Upload" addclass="mt-3" />
                          </>
                        )}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              {/* {errors.video && (
              <p className="text-red-500 text-sm mt-1">
                {(errors.video as FieldError)?.message}
              </p>
            )} */}
            </div>
          </div>

          <CustomButton
            type="submit"
            btntext="Submit"
            isLoading={isPending || isMultipleUploading || isUploading}
            addclass="mt-9 mx-auto w-1/2"
          />
        </form>
      )}
      <FileUploadModal
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        onFileUpload={handleFileUpload}
      />{" "}
    </Sidebar>
  );
};

export default UpdateExercises;
